import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, ArrowLeft, Phone } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { BUSINESS } from "@/data";

const inputCls =
  "w-full rounded-none border-0 border-b-2 border-black bg-transparent py-3 px-1 text-base placeholder:text-black/40 focus:outline-none focus:border-[#FF4E00] transition-colors min-h-[44px]";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#0A0A0A] text-white" data-testid="login-page">
      <div className="mx-auto grid min-h-screen max-w-[1400px] grid-cols-1 lg:grid-cols-2">
        <div className="hidden flex-col justify-between border-r border-white/15 p-12 lg:flex">
          <Link to="/" data-testid="login-home-link" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-white">
            <ArrowLeft size={16} /> Back to site
          </Link>
          <div>
            <h1 className="font-anton text-7xl uppercase leading-[0.85] tracking-tighter">
              Owner<br /><span className="text-[#FF4E00]">Dashboard</span>
            </h1>
            <p className="mt-6 max-w-sm text-white/60">Log in to view and manage incoming quote requests for {BUSINESS.name}.</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/40">
            <Phone size={14} /> {BUSINESS.phone}
          </div>
        </div>

        <div className="flex items-center justify-center bg-white px-4 py-16 text-black sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-sm"
          >
            <div className="mb-8 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center bg-[#FF4E00] text-white">
                <Lock size={22} />
              </div>
              <div>
                <div className="font-anton text-2xl uppercase leading-none">Sign In</div>
                <div className="text-xs uppercase tracking-[0.15em] text-black/50">Staff & Owner</div>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="login-form">
              <input data-testid="login-email" type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
              <input data-testid="login-password" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />

              {error && <p data-testid="login-error" className="text-sm font-bold text-red-600">{error}</p>}

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="hard-shadow flex w-full items-center justify-center gap-2 bg-black px-8 py-5 font-anton text-xl uppercase tracking-wide text-white transition-transform hover:translate-x-1 hover:translate-y-1 hover:shadow-none disabled:opacity-60"
              >
                {loading ? <><Loader2 className="animate-spin" size={20} /> Signing in…</> : "Log In"}
              </button>
            </form>

            <Link to="/" className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-black/50 transition-colors hover:text-[#FF4E00] lg:hidden">
              <ArrowLeft size={14} /> Back to site
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
